import { storage } from '../../lib/firebase';
import { ref, uploadBytes, deleteObject, getDownloadURL } from 'firebase/storage';
import { convertToWebP, generateCleanFilename } from '../../utils/image';
import { isProduction } from '../../config/environment';

interface UseOrganizationFaviconReturn {
  uploadFavicon: (file: File, orgId: string, currentFaviconUrl?: string) => Promise<string>;
}

export const useOrganizationFavicon = (): UseOrganizationFaviconReturn => {
  const uploadFavicon = async (
    file: File,
    orgId: string,
    currentFaviconUrl?: string
  ): Promise<string> => {
    try {
      // Convert image to WebP
      const webpBlob = await convertToWebP(file);

      // Generate clean filename with organization ID in path
      const filename = generateCleanFilename(file.name, orgId);
      const fullPath = isProduction()
        ? `favicons/${filename}` // Production: favicons folder in heyisa-media bucket
        : `speculo-onboarding/favicons/${filename}`; // Staging/Dev: speculo-onboarding bucket

      // Delete old favicon if it exists
      if (currentFaviconUrl) {
        if (currentFaviconUrl.includes('favicons')) {
          try {
            // Extract the path from emulator URL format
            const match = currentFaviconUrl.match(/favicons%2F(.*?)(?:\?|$)/);

            if (match && match[1]) {
              const currentPath = isProduction()
                ? `heyisa-media/favicons/${decodeURIComponent(match[1])}`
                : `favicons/${decodeURIComponent(match[1])}`;
              const currentRef = ref(storage, currentPath);
              await deleteObject(currentRef);
            }
          } catch (error) {
            console.warn('Could not delete old favicon:', error);
            // Continue with upload even if delete fails
          }
        }
      }

      // Create storage reference based on environment
      const storageRef = isProduction()
        ? ref(storage, `heyisa-media/${fullPath}`) // Production: heyisa-media/favicons/
        : ref(storage, fullPath); // Staging/Dev: speculo-onboarding/favicons/
      await uploadBytes(storageRef, webpBlob, {
        contentType: 'image/webp',
      });

      // Get the download URL
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.error('Error handling favicon upload:', error);
      throw new Error('Failed to upload favicon');
    }
  };

  return { uploadFavicon };
};
